import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { OptionsDocument, PresentationOptionV2, PresentationPreview, QuoteDocument } from 'paintscout'
import classnames from 'classnames'
import * as Sentry from '@sentry/core'
import { QuoteContextProvider, useQuote } from '../../context'
import {
  getPresentationLogo,
  getPresentationPreviewQuote,
  resolvePageForKey,
  CLOSED_STATUSES
} from '@paintscout/util/builder'
import CustomerViewContents from '../CustomerViewContents'
import {
  createTheme,
  ThemeProvider,
  makeStyles,
  CssBaseline,
  CircularProgress,
  jssPreset,
  StylesProvider,
  PdfOwnerDocumentProvider
} from '@paintscout/ui'
import { ClientOptionsProvider, DialogStackProvider, useClientOptions, useUser } from '@paintscout/ui-business'
import { CVLogo, SpinningLogo } from '../components'
import { create } from 'jss'
import Frame, { FrameContextConsumer } from 'react-frame-component'
import type { MuiTheme as Theme } from '@paintscout/ui'
import imgComparisonSliderSource from 'img-comparison-slider/dist/index.js?raw'
import {
  getHeaderHeight,
  getPresentationDeviceWidth,
  getPresentationFrameWidth,
  scrollToPresentationKey
} from '../util'
import {
  arePreviewPropsEqual,
  PRESENTATION_PREVIEW_MIN_LOADING_MS,
  shouldShowPreviewLoading
} from './presentationPreviewLoading'

/** How long the anchor block stays pinned after a device switch. Covers the nav header's 1s height transition. */
const DEVICE_SWITCH_SETTLE_MS = 1000

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative'
    },
    desktopPreviewRoot: {
      flexGrow: 1
    },
    deviceWrapper: {
      // this is used to create the pseudo element for the notch since frames cannot have PEs
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      '&::before': {
        zIndex: 4,
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        top: 10,
        width: 40,
        height: 4,
        backgroundColor: theme.palette.common.white
      }
    },
    deviceFrame: {
      border: '8px solid',
      borderTop: '24px solid',
      borderColor: theme.palette.grey[400],
      borderRadius: theme.borderRadius['lg']
    },
    minimalWrapper: {
      width: '100%',
      // was text-align:center from when desktop's inner frame was always width:'100%' (so centering
      // never mattered); now that desktop is a fixed 1180px like every other mode, this has to
      // actually center a block-level child
      display: 'flex',
      justifyContent: 'center'
    },
    minimalFrame: {
      border: `none`,
      borderRadius: 4,
      boxShadow: 'rgba(16, 24, 40, 0.05) 0px 1px 2px, rgba(16, 24, 40, 0.043) 0px 10px 28px'
    },
    noFrame: {
      border: 'none'
    },
    loadingScreen: {
      alignItems: 'center',
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      transition: 'top 1000ms cubic-bezier(0.76, 0, 0.24, 1), opacity 1000ms cubic-bezier(0.32, 0, 0.67, 0)',
      top: 0,
      pointerEvents: 'none',
      position: 'absolute',
      width: '100%',
      opacity: 1
    },
    appLoadingDone: {
      opacity: 0
    },
    cvLoadingDone: {
      top: '-100%'
    }
  }),
  { name: 'PresentationImbedPreview' }
)

export interface PresentationPreviewProps {
  border: 'minimal' | 'device' | 'none'
  loadingStyle: 'app' | 'cv' | 'none'
  classes?: any
  presentation?: PresentationOptionV2
  preview?: PresentationPreview
  quoteId?: string
  logoSrc?: string
  handleAcceptClick?: (ev: React.MouseEvent<HTMLElement, MouseEvent>) => void
  isPreviewAccept?: boolean
  onDismissDialog?: (event: any, reason: 'backdropClick' | 'escapeKeyDown') => void
  autoHeight?: boolean
  autoHeightCallback?: React.Dispatch<React.SetStateAction<number>>
  hideFooter?: boolean
  hideNav?: boolean
  /** Skips the accepted/paid/invoiced status badge — for previews that don't carry a real quote,
   * e.g. a section-template thumbnail gallery, where the placeholder demo quote's hardcoded
   * 'accepted' status would otherwise show through (#22683). */
  hideAcceptedBadge?: boolean
  preventScroll?: boolean
  /**
   * Swallow every click inside the frame, so the preview is look-only (ENG-2015).
   *
   * The v1 previews get this from `pointer-events: none` on their wrapper. The iframe can't take
   * that — it would stop scrolling too — so clicks reached the customer view: an item's photos
   * opened the lightbox and picture blocks opened the media viewer, over the preview. Scrolling
   * still works. Leave unset wherever the preview must respond (accept, nav, package picks).
   */
  disableClicks?: boolean
  /**
   * Open on this section instead of at the top of the first page (#23716).
   *
   * The v2 editor sets it to whatever section was in view on its canvas when preview was switched
   * on. It seeds the page (resolved with `resolvePageForKey`, since only the active page's
   * sections are ever in the DOM) and is scrolled to once the frame finishes loading. Every other
   * caller leaves it unset and keeps the old page-1 behaviour.
   */
  anchorKey?: string
  /**
   * The editor was scrolled fully to the top when the anchor was captured (ENG-2004/#23984). The
   * frame then stays at the top instead of aligning `anchorKey` under the header; the key still
   * seeds the page. Ignored without `anchorKey`.
   */
  anchorAtTop?: boolean
  /**
   * Load every prefab and curated font family into the iframe on top of the ones this
   * presentation's own style names. Expensive — about 20 families at six variants each — so it is
   * opt-in, for the one surface that swaps between several styles inside a single preview
   * (`NewPresentationDialog`). Leaving it off makes the preview load exactly what the customer
   * view loads (#22535).
   */
  loadAllFonts?: boolean
  usePresentationFromProp?: boolean
  /**
   * Raise a quote the preview's own contents changed, so the caller can hold the same copy.
   *
   * The customer can pick a Good/Better/Best package from inside the preview (the totals panel's
   * Switch), and that write has nowhere to go otherwise: this component seeds its quote from
   * context once and the surrounding editor never hears about it, so the choice rendered here would
   * not be the one accepted (ENG-1624). Providing this also switches `updateQuote` on inside the
   * preview's own context — without it that write stays the no-op every other preview surface
   * expects.
   */
  onQuoteChange?: (quote: QuoteDocument) => void
  /**
   * Kept in sync with the iframe's own `document`/`window` on every render, so a caller elsewhere in
   * the tree can read the preview's current scroll position at click time — used to capture the
   * anchor when leaving preview back to the editor (ENG-2004/#23984). Optional: every other caller
   * of this component has no such use for it.
   */
  frameRef?: React.MutableRefObject<{ doc: Document; win: Window } | null>
  teaserProps?: {
    options: OptionsDocument
    quote: QuoteDocument
    loading?: boolean
    banner?: React.ReactNode
  }
}

const PresentationImbedPreview = (props: PresentationPreviewProps) => {
  const classes = useStyles(props)
  const {
    anchorKey,
    anchorAtTop,
    autoHeight: useAutoHeight,
    autoHeightCallback,
    border,
    disableClicks,
    frameRef: externalFrameRef,
    handleAcceptClick,
    hideFooter,
    hideNav,
    hideAcceptedBadge,
    isPreviewAccept,
    loadAllFonts,
    loadingStyle,
    logoSrc,
    onDismissDialog,
    presentation,
    preventScroll,
    preview,
    usePresentationFromProp,
    onQuoteChange,
    teaserProps
  } = props
  // have two quote options so it works w/ and w/o context
  const { quote: contextQuote, estimator, wisetackPromo } = useQuote()
  // The preview builds its own QuoteContext, so anything the surrounding editor put there is
  // lost unless it is carried across: the Wisetack promo the editor fetched, and the fact that
  // this renders the customer-facing document. Without isPresentation the document renders in
  // its merchant form — and the Wisetack apply button stays live instead of inert (ENG-833).
  // Matches what the v1 QuoteNextPreviewDialog passes. Memoized — QuoteContextProvider keys its
  // value memo on this object's identity.
  //
  // `updateQuote` is added only when the caller asked for it (`onQuoteChange`), so every other
  // preview surface keeps the no-op it has always had.
  const previewContextValue = useMemo(
    () =>
      onQuoteChange
        ? { wisetackPromo, isPresentation: true, updateQuote: ({ quote: updated }) => onQuoteChange(updated) }
        : { wisetackPromo, isPresentation: true },
    [wisetackPromo, onQuoteChange]
  )
  const initQuote = teaserProps?.quote ?? contextQuote ?? getPresentationPreviewQuote()
  const [localQuote, setQuote] = useState(initQuote)
  /*
   * Controlled when the caller passes `onQuoteChange`: that caller holds the document (it is the
   * one being accepted), so the preview renders straight from the prop rather than from a copy
   * seeded once at mount. Without this a package chosen inside the preview updated the caller and
   * the preview kept showing the tier it started on — the local copy has no path back from an
   * outside change (ENG-1624).
   *
   * Uncontrolled otherwise, which is every other preview surface: no owner outside, so the local
   * copy stays the document.
   */
  const quote = onQuoteChange ? initQuote : localQuote
  const { options: contextOptions } = useClientOptions()
  const options = teaserProps?.options ?? contextOptions
  const { isCustom } = getPresentationLogo({ options, quote })
  const { user } = useUser()
  const companyId = user?.app_metadata?.companyId
  const isCustomLogo = isCustom && companyId

  // MUI's createTheme walks and merges its whole default theme, so it must not run on every
  // render — the JSS registry built inside the frame below depends on it (#22535).
  const baseTheme = useMemo(() => createTheme(), [])
  // Space between the fixed header and the anchored section. Zero: the section the editor was
  // viewing sits flush under the header, at the top of the visible frame (ENG-2004/#23984).
  const anchorGutter = 0

  const [navOpen, setNavOpen] = useState(false)
  // Lazy initializer, and the *anchor's* page rather than `pages[0]`: without it the preview always
  // opened on page 1 no matter which page the editor was showing (#23716). `resolvePageForKey` is
  // the same resolution `Presentation` runs for its own `activePage`, so a stale or absent
  // `anchorKey` falls back to the first page exactly as before.
  const [currentPage, setCurrentPage] = useState<string>(
    () => resolvePageForKey(presentation, anchorKey)?.key ?? presentation.pages[0].key
  )
  const [autoHeight, setAutoHeight] = useState(0)
  const [loading, setLoading] = useState(true)
  const [minDurationReached, setMinDurationReached] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMinDurationReached(true)
    }, PRESENTATION_PREVIEW_MIN_LOADING_MS)
    return () => clearTimeout(timeout)
  }, [])
  const showLoading = shouldShowPreviewLoading({
    loadingStyle,
    minDurationReached,
    contentLoading: loading,
    teaserLoading: teaserProps?.loading
  })

  let width: number | string, height: number | string

  switch (preview) {
    case 'mobile':
      width = getPresentationDeviceWidth(preview)
      height = 622
      break

    case 'tablet':
      width = getPresentationDeviceWidth(preview)
      height = 1024
      break

    case 'desktop':
      width = getPresentationDeviceWidth(preview)
      height = '100%'
      break

    default:
      width = '100%'
      height = '100%'
      break
  }

  function getHeight(height: number) {
    if (!useAutoHeight) return null
    if (autoHeightCallback) autoHeightCallback(height)
    setAutoHeight(height)
  }

  // Refs, not render-body `let`s: the poller is started from the <Frame> render callback below,
  // which runs on every render, so plain locals reset to undefined each time and started a new
  // 100ms interval per render that nothing owned (#22535).
  const fontIntervalRef = useRef<ReturnType<typeof setInterval>>(null)
  const fontAttemptsRef = useRef(0)
  const jssRef = useRef<{ doc: Document; jss: ReturnType<typeof create> }>({ doc: null, jss: null })
  // The frame's own document/window, only reachable from inside <FrameContextConsumer> — kept here
  // so the anchor scroll below can run from an effect instead of from the render callback.
  //
  // The anchor effect reads this ref, and a ref written during render is normally a race with the
  // effect that reads it. It is not one here: the effect fires on `loading` turning false, and only
  // `checkFont` clears `loading` — a poller that the same render callback starts, on the same
  // `window` it stores here. So the write always precedes the first read. The effect still guards
  // on `frameRef.current` rather than relying on that.
  const frameRef = useRef<{ doc: Document; win: Window }>(null)
  const anchorScrolledRef = useRef(false)

  const checkFont = (frameWindow: Window) => {
    fontAttemptsRef.current++
    if (!frameWindow.onload) {
      frameWindow.onload = () => {}
    }
    const fontsLoaded = frameWindow.sessionStorage?.getItem('fontsLoaded')
    if (fontsLoaded || fontAttemptsRef.current > 99) {
      if (!fontsLoaded) {
        // loadPresentationFonts always sets this flag, even for a presentation with no
        // fetchable families (#22535) — reaching the poll cap without it means the load itself
        // failed silently (network error, WebFontLoader throw), so the preview reveals
        // un-styled content with no other signal. Surface it rather than fail silently.
        Sentry.captureMessage('PresentationImbedPreview: font poll timed out without fontsLoaded', 'warning')
      }
      setLoading(false)
      stopFontCheck()
    }
  }

  function stopFontCheck() {
    if (fontIntervalRef.current) {
      clearInterval(fontIntervalRef.current)
      fontIntervalRef.current = null
    }
  }

  function startFontCheck(frameWindow: Window) {
    if (fontIntervalRef.current) return
    fontIntervalRef.current = setInterval(checkFont, 100, frameWindow)
  }

  useEffect(() => () => stopFontCheck(), [])

  // the switch above sets width from getPresentationDeviceWidth(preview), so the frame helper adds
  // its borders to that same canvas width — the two can't drift.
  const deviceWidth = width === '100%' ? width : getPresentationFrameWidth(preview)
  const deviceHeight = height === '100%' ? height : Number.parseInt(height.toString()) + (border === 'device' ? 32 : 0)

  function getLoadingIcon() {
    if (loadingStyle === 'app') {
      return <SpinningLogo scale={0.3} />
    } else if (loadingStyle === 'cv') {
      return isCustomLogo ? (
        <CircularProgress size={50} />
      ) : (
        <CVLogo customLogoSrc={logoSrc} fallback={<SpinningLogo scale={0.3} />} label={'Loading...'} />
      )
    } else {
      return null
    }
  }

  /**
   * Scrolls the frame to `anchorKey` once, after its content is on screen (#23716).
   *
   * Gated on `loading` rather than on mount: the sections have no height until the fonts resolve
   * and the customer view renders, so an earlier scroll lands on a document that is still empty.
   * The ref guard keeps it to one attempt — switching device inside preview does not remount this
   * component, and re-running would yank the frame back to the anchor after the user has scrolled
   * away from it.
   */
  useEffect(() => {
    if (loading || !anchorKey || preventScroll || anchorScrolledRef.current || !frameRef.current) {
      return
    }

    // the frame opens at the top, which is where the editor was: latch so a later anchor change
    // (a device switch) does not run this first-scroll logic
    if (anchorAtTop) {
      anchorScrolledRef.current = true
      return
    }

    const { doc, win } = frameRef.current
    const headerHeight = doc.getElementsByTagName('header')?.[0]?.offsetHeight ?? 0

    scrollToPresentationKey({ key: anchorKey, headerHeight, gutter: anchorGutter, doc, win })

    // after the call, not before: a throw would otherwise leave the ref latched and block a retry
    anchorScrolledRef.current = true
  }, [loading, anchorKey, anchorAtTop, preventScroll, anchorGutter])

  /**
   * Keeps the anchor block at the top of the frame through a device switch inside preview
   * (ENG-2004/#23984).
   *
   * Resizing the frame reflows every section, so the block that was at the top lands somewhere
   * else. The caller re-reads that block at the old width and passes it as `anchorKey` in the same
   * render that changes `preview`.
   *
   * A layout effect, so the first alignment happens before the browser paints the reflowed frame.
   * Then the block is re-pinned every frame for `DEVICE_SWITCH_SETTLE_MS`, because the frame is not
   * final at commit: the nav header animates its height, breakpoint media queries re-render after
   * the resize event, and responsive images swap `srcset` candidates. A one-shot scroll after two
   * frames landed before those finished. The scroll is instant so the reflow is not animated, and
   * the user taking over (wheel, touch or keys) ends the pin. Skipped on mount: the effect above
   * owns that.
   *
   * The deps are `[preview]` on purpose. The effect is a reaction to the device changing, and every
   * other value it reads (`anchorKey`, `anchorAtTop`, `loading`, …) is the one from that same
   * render. Listing them would run the cleanup — cancelling an in-flight pin — whenever one of them
   * changed. A switch that lands while `loading` is true is skipped by design: there is no layout to
   * hold yet, and the first-scroll effect above owns that case. The cleanup also runs on unmount.
   */
  const previousPreviewRef = useRef(preview)
  useLayoutEffect(() => {
    if (previousPreviewRef.current === preview) {
      return
    }

    previousPreviewRef.current = preview

    if (loading || !anchorKey || preventScroll || !frameRef.current) {
      return
    }

    const { doc, win } = frameRef.current
    let frame = 0

    const align = () => {
      // fully scrolled up before the switch: stay fully scrolled up
      if (anchorAtTop) {
        win.scrollTo({ top: 0, behavior: 'auto' })
        return
      }

      // a page change or a re-render can drop the element for a moment; scrollToPresentationKey
      // would send the frame to the top instead of leaving it alone
      if (!doc.getElementById(anchorKey)) {
        return
      }

      scrollToPresentationKey({
        key: anchorKey,
        headerHeight: getHeaderHeight(doc),
        gutter: anchorGutter,
        doc,
        win,
        behavior: 'auto'
      })
    }

    const stop = () => cancelAnimationFrame(frame)
    const startedAt = performance.now()
    const tick = () => {
      align()

      if (performance.now() - startedAt < DEVICE_SWITCH_SETTLE_MS) {
        frame = requestAnimationFrame(tick)
      }
    }

    tick()

    const takeOverEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const
    takeOverEvents.forEach((name) => win.addEventListener(name, stop, { passive: true }))

    return () => {
      stop()
      takeOverEvents.forEach((name) => win.removeEventListener(name, stop))
    }
  }, [preview])

  useEffect(() => {
    // reset quote when status changes from closed to open, e.g., quote is accepted
    if (!CLOSED_STATUSES.includes(quote.status.value) && CLOSED_STATUSES.includes(initQuote.status.value)) {
      setQuote(initQuote)
    }
  }, [initQuote.status.value])

  return (
    <div className={classnames(classes.root, { [classes.desktopPreviewRoot]: preview === 'desktop' })}>
      <div
        className={classnames({
          [classes.deviceWrapper]: border === 'device',
          [classes.minimalWrapper]: border === 'minimal' || border === 'none'
        })}
      >
        <div
          style={{
            width: deviceWidth,
            height: useAutoHeight ? autoHeight : deviceHeight,
            position: 'relative',
            overflow: 'hidden'
          }}
          className={classnames({
            [classes.deviceFrame]: border === 'device',
            [classes.minimalFrame]: border === 'minimal',
            [classes.noFrame]: border === 'none'
          })}
        >
          {loadingStyle !== 'none' && (
            <div
              className={classnames(classes.loadingScreen, {
                [classes.appLoadingDone]: !showLoading && loadingStyle === 'app',
                [classes.cvLoadingDone]: !showLoading && loadingStyle === 'cv'
              })}
            >
              {getLoadingIcon()}
            </div>
          )}
          <Frame
            id={'component-frame'}
            style={{ border: 'none' }}
            width={width}
            height={useAutoHeight ? autoHeight : height}
          >
            <FrameContextConsumer>
              {({ document, window }) => {
                startFontCheck(window as any)

                // The frame's own document and window. They are reachable only from inside this
                // callback, so the anchor-scroll effect reads them from here (#23716).
                frameRef.current = { doc: document, win: window }
                // Mirrored onto the caller's own ref, when given, so leaving preview can read the
                // iframe's current scroll position at click time (ENG-2004/#23984) — see this prop's
                // jsdoc above.
                if (externalFrameRef) {
                  externalFrameRef.current = { doc: document, win: window }
                }

                // A fresh JSS registry makes MUI regenerate every class in the iframe, so it is
                // built once per iframe document rather than once per render (#22535).
                if (jssRef.current.doc !== document) {
                  jssRef.current = {
                    doc: document,
                    jss: create({
                      plugins: [...jssPreset().plugins],
                      insertionPoint: document.head
                    })
                  }
                }
                const jss = jssRef.current.jss

                const styleEl = document.getElementById('appStyles')
                if (!styleEl) {
                  const style = document.createElement('style')
                  style.id = 'appStyles'
                  style.appendChild(document.createTextNode(appStyles()))
                  document.head.appendChild(style)
                }

                // The iframe is a fresh document: it gets neither the parent page's Google
                // Fonts <link> (Inter) nor its bundled @font-face rules (Euclid, CustomFont-SW).
                // Euclid's woff2 files are Vite-hashed assets with no stable URL, so copy the
                // parent's resolved @font-face rules instead of hardcoding paths. CustomFont-SW
                // (Sherwin-Williams partner sections, #21856) is included here too — without it
                // SW headings silently fall back to a generic font inside the preview iframe.
                const parentFontsEl = document.getElementById('parentFontFaces')
                if (!parentFontsEl) {
                  const css = getParentFontFaceCss(['Euclid', 'CustomFont-SW'])
                  if (css) {
                    const style = document.createElement('style')
                    style.id = 'parentFontFaces'
                    style.appendChild(document.createTextNode(css))
                    document.head.appendChild(style)
                  }
                }

                const interLinkEl = document.getElementById('interFontLink')
                if (!interLinkEl) {
                  const link = document.createElement('link')
                  link.id = 'interFontLink'
                  link.rel = 'stylesheet'
                  link.href =
                    'https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap'
                  document.head.appendChild(link)
                }

                // The SliderImage section's <img-comparison-slider> custom element only ever
                // registers against whichever window/document first executed the app bundle —
                // the parent page, not this iframe. Custom element classes can't be re-registered
                // across realms (the class's HTMLElement base is tied to the realm it was defined
                // in), so without this the element never upgrades here and silently renders as
                // two stacked plain images with no slider behavior. Re-running the library's own
                // source as a real <script> inside this document defines it fresh against the
                // iframe's own window, exactly as if the iframe had loaded the bundle itself.
                const sliderScriptEl = document.getElementById('imgComparisonSliderScript')
                if (!sliderScriptEl) {
                  const script = document.createElement('script')
                  script.id = 'imgComparisonSliderScript'
                  script.textContent = imgComparisonSliderSource
                  document.head.appendChild(script)
                }

                // A capture listener on the frame's window runs before React's own listener on the
                // portal container, so nothing inside the frame ever sees the click.
                if (disableClicks && !document.getElementById('disableClicksStyles')) {
                  window.addEventListener(
                    'click',
                    (ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                    },
                    true
                  )
                  const style = document.createElement('style')
                  style.id = 'disableClicksStyles'
                  style.appendChild(document.createTextNode('* { cursor: default !important; }'))
                  document.head.appendChild(style)
                }

                if (preventScroll) {
                  const body = document.getElementsByTagName('body')[0]
                  body.style.overflow = 'hidden'
                }

                // Preview/Accept never wired a more-actions handler through, so the button hides
                // itself entirely (PresentationAcceptButton's hasMoreButton = !!onMoreClick) no
                // matter what the presentation contains (#22699 follow-up). Print is the only
                // action that makes sense here: download needs a persisted quote _id/_rev the
                // preview may not have yet, and decline makes no sense previewing your own draft
                // (excluded separately via isPreviewAccept in PresentationAcceptButton).
                function handleMoreClick(ev: React.MouseEvent, action: string) {
                  if (action === 'print' && !!window?.print) {
                    window.print()
                  } else if (action !== 'print') {
                    // Download/Decline are hidden in this context (see PresentationAcceptButton's
                    // isPreviewAccept gate), so this only fires if a future action type is added
                    // to the menu without a preview-context handler for it — surface that instead
                    // of silently doing nothing.
                    console.warn(`PresentationImbedPreview: no handler for More Actions action "${action}"`)
                  }
                }

                function handlePageClick(ev: React.MouseEvent, key: string) {
                  setCurrentPage(key)
                  setNavOpen(false)

                  // The nav emits a page key when multi-page and a section key when single-page
                  // (#21990). A section is on the page already on screen, so scroll to it; a page
                  // swaps what renders, so there's nothing to scroll to — scrollToPresentationKey
                  // falls back to the top when `key` isn't found in this document.
                  const headerHeight = document.getElementsByTagName('header')?.[0]?.offsetHeight ?? 0
                  scrollToPresentationKey({
                    key,
                    headerHeight,
                    gutter: baseTheme.spacing(4),
                    doc: document,
                    win: window
                  })
                }

                return (
                  <StylesProvider jss={jss}>
                    <ThemeProvider theme={baseTheme}>
                      {/*
                        PdfOwnerDocumentProvider wraps DialogStackProvider (not the other way
                        around): DialogStackProvider renders its open-dialog stack as a sibling of
                        its children, not a descendant (see the same gotcha documented in
                        apps/customer-view/src/components/ViewProvider.tsx), so a dialog opened
                        here (e.g. ViewMediaDialog's PDF viewer) would otherwise never see the
                        iframe's document and would fall back to the parent document for pdf.js's
                        font loading — reintroducing the tofu-glyph bug for dialogs specifically.
                      */}
                      <PdfOwnerDocumentProvider document={document}>
                        <DialogStackProvider>
                          <ClientOptionsProvider options={options}>
                            <CssBaseline />
                            <QuoteContextProvider
                              quote={{ ...quote, presentationTemplate: presentation }}
                              estimator={estimator}
                              contextValue={previewContextValue}
                            >
                              <CustomerViewContents
                                allowResponse={!!handleAcceptClick}
                                banner={teaserProps?.banner}
                                currentPage={currentPage}
                                getHeight={getHeight}
                                handleAcceptClick={handleAcceptClick}
                                handleMoreClick={handleMoreClick}
                                handlePageClick={(ev, key) => handlePageClick(ev, key)}
                                handleSectionClick={() => null}
                                hideFooter={hideFooter}
                                hideNav={hideNav}
                                hideAcceptedBadge={hideAcceptedBadge}
                                isInvoice={quote?.is_invoice}
                                isPreview={true}
                                isPreviewAccept={isPreviewAccept}
                                loadAllFonts={loadAllFonts}
                                navOpen={navOpen}
                                onDismissDialog={onDismissDialog}
                                presentation={presentation}
                                preview={preview}
                                setNavOpen={setNavOpen}
                                usePresentationFromProp={usePresentationFromProp}
                                view={'quote'}
                              />
                            </QuoteContextProvider>
                          </ClientOptionsProvider>
                        </DialogStackProvider>
                      </PdfOwnerDocumentProvider>
                    </ThemeProvider>
                  </StylesProvider>
                )
              }}
            </FrameContextConsumer>
          </Frame>
        </div>
      </div>
    </div>
  )
}

/**
 * Memoized with a comparator that deep-compares `presentation` and `classes` — callers resolve
 * both in a render body, so a reference compare never matches and every keystroke in the
 * surrounding form re-rendered the whole iframe (#22535). `SendQuoteDialog` already wrapped its
 * own use of this component for the same reason; this covers the rest, including the quote's
 * Presentation tab.
 */
export default React.memo(PresentationImbedPreview, arePreviewPropsEqual)

/**
 * Serialize the parent document's @font-face rules for the given families so they can
 * be re-declared inside the preview iframe. CSSOM's cssText carries the resolved
 * (bundler-hashed) font URLs, which have no stable path we could hardcode.
 *
 * NOTE: runs in the parent window scope — `document` here is the parent document,
 * not the iframe's.
 */
function getParentFontFaceCss(families: string[]): string {
  const rules: string[] = []
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      let cssRules: CSSRuleList
      try {
        cssRules = sheet.cssRules
      } catch {
        continue // cross-origin stylesheet (e.g. Google Fonts <link>) — not readable
      }
      for (const rule of Array.from(cssRules)) {
        if (rule.type === CSSRule.FONT_FACE_RULE) {
          const family = (rule as CSSFontFaceRule).style.getPropertyValue('font-family').replaceAll(/['"]/g, '').trim()
          if (families.includes(family)) {
            rules.push(rule.cssText)
          }
        }
      }
    }
  } catch {
    // font copying must never break the preview itself
  }
  return rules.join('\n')
}

function appStyles() {
  // duplicate of apps/stickybid/src/index.css, minus stickybid's degular @font-face rules and
  // font-family override — PaintScout doesn't use or load that font
  return `
  html {
    font-size: 16px;
  }

  body {
    background-color: #fff !important;
    line-height: 1.42857143;
  }
  
  /* :focus-visible polyfill to remove hide focus when interacted with mouse */
  button:focus:not(.focus-visible) {
    outline: none;
  }
  
  @media print {
    body {
      min-width: 1024px;
      border-bottom: 1px solid transparent;
    }
  }
  
  strong {
    font-weight: 500;
  }
  
  fieldset {
    border: 0;
    padding: 0.01em 0 0 0;
    margin: 0;
    min-width: 0;
  }
  
 `
}
